import React from 'react';

import { Box, Card, CardProps, Typography } from '@mui/material';
import { CircleDollarSign as PaymentIcon } from 'lucide-react';
import { PaymentListItem } from '../../../apis/paymentApi';
import { getPaymentDisplayStatus, paymentStatusColors } from '../utils/paymentUtils';
import CardLabel from '../../../components/common/CardLabel';
import { formatDateShort } from '../../../utils/formatUtils';

type PaymentCardProps = CardProps & { row: PaymentListItem };

const PaymentCard: React.FC<PaymentCardProps> = ({ row }: PaymentCardProps) => {
  const paymentStatus = getPaymentDisplayStatus(row);

  return (
    <Card
      sx={{
        mx: 2,
        mt: 2,
        mb: 1,
        display: 'flex',
        border: '1px solid',
        borderColor: (theme) => theme.palette.divider,
      }}
    >
      <Box
        sx={{
          width: 35,
          minWidth: 35,
          minHeight: 60,
          maxHeight: 190,
          overflow: 'hidden',
        }}
      >
        <Box
          height="100%"
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          title={paymentStatus}
          color={(theme) => theme.palette.common.white}
          sx={{
            backgroundColor: () => paymentStatusColors[row.status.toLowerCase() as keyof typeof paymentStatusColors],
          }}
        >
          <PaymentIcon width="60%" height="60%" />
        </Box>
      </Box>
      <Box pt={2} pl={3} pr={1} pb={1} flexShrink={1} flexGrow={1}>
        <Typography variant="h6" mb="6px">
          {row.method} payment: {paymentStatus}
        </Typography>
        <Typography
          mb="4px"
          mr={2}
          variant="body2"
          sx={{
            display: 'inline-block',
            fontSize: 14,
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          <CardLabel>Total:</CardLabel> ${row.amount.toFixed(2)}
        </Typography>
        {row.paymentDate && (
          <Typography
            mb="4px"
            variant="body2"
            sx={{
              display: 'inline-block',
              fontSize: 14,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            <CardLabel>Date:</CardLabel> {formatDateShort(row.paymentDate)}
          </Typography>
        )}
        <Box>
          {row.invoiceNumber && (
            <Typography
              mb="3px"
              mr={2}
              variant="body2"
              sx={{
                display: 'inline-block',
                fontSize: 14,
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              <CardLabel>Invoice:</CardLabel> {row.invoiceNumber}
            </Typography>
          )}
          {row.last4 && (
            <Typography
              mb="3px"
              variant="body2"
              sx={{
                display: 'inline-block',
                fontSize: 14,
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              <CardLabel>Account:</CardLabel> {row.last4}
            </Typography>
          )}
        </Box>
      </Box>
      <Box width={20} pl={2} mr={3} display="flex">
        &nbsp;
      </Box>
    </Card>
  );
};

export default PaymentCard;
